<template>
  <div class="google-autocomplete-wrapper">
    <!-- Try to use vuetify-google-autocomplete first -->
    <vuetify-google-autocomplete
      v-if="autocompleteLoaded && !useFallback"
      v-model="searchValue"
      :api-key="apiKey"
      :version="version"
      :placeholder="placeholder"
      :types="types"
      :country="country"
      :component-restrictions="componentRestrictions"
      @place_changed="onPlaceChanged"
      @error="onError"
      class="google-autocomplete-input"
    />

    <!-- Fallback to regular input if autocomplete fails -->
    <v-text-field
      v-else
      v-model="searchValue"
      :placeholder="placeholder"
      :label="label"
      :rules="rules"
      :disabled="disabled"
      :readonly="readonly"
      :clearable="clearable"
      :outlined="outlined"
      :filled="filled"
      :solo="solo"
      :dense="dense"
      :hide-details="hideDetails"
      :error="error"
      :error-messages="errorMessages"
      @input="onInput"
      @change="onChange"
      class="fallback-input"
    >
      <template v-slot:append>
        <v-icon>mdi-map-marker</v-icon>
      </template>
    </v-text-field>
  </div>
</template>

<script>
export default {
  name: 'GoogleAutocomplete',
  props: {
    value: {
      type: String,
      default: ''
    },
    apiKey: {
      type: String,
      required: true
    },
    version: {
      type: String,
      default: '3'
    },
    placeholder: {
      type: String,
      default: 'Enter address'
    },
    label: {
      type: String,
      default: 'Address'
    },
    types: {
      type: Array,
      default: () => ['address']
    },
    country: {
      type: [String, Array],
      default: ''
    },
    componentRestrictions: {
      type: Object,
      default: () => ({})
    },
    // Bounds to bias/restrict results: { south, north, west, east } in degrees
    bounds: {
      type: Object,
      default: null
    },
    rules: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    outlined: {
      type: Boolean,
      default: true
    },
    filled: {
      type: Boolean,
      default: false
    },
    solo: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: [String, Array],
      default: () => []
    }
  },
  data () {
    return {
      searchValue: this.value,
      autocompleteLoaded: false,
      googleMapsLoaded: false,
      useFallback: false
    }
  },
  watch: {
    value (newVal) {
      this.searchValue = newVal
    },
    searchValue (newVal) {
      this.$emit('input', newVal)
    }
  },
  mounted () {
    this.checkAutocompleteAvailability()
    this.loadGoogleMaps()
  },
  methods: {
    checkAutocompleteAvailability () {
      // Check if vuetify-google-autocomplete component is available and working
      try {
        // Check if the component is registered
        if (this.$options.components['vuetify-google-autocomplete']) {
          // Check if it's not the dummy component by looking for render function
          const component = this.$options.components['vuetify-google-autocomplete']
          if (component.render && typeof component.render === 'function') {
            // Check if it's our dummy component (has mounted hook with warning)
            if (component.mounted && component.mounted.toString().includes('vuetify-google-autocomplete component is not available')) {
              this.useFallback = true
              console.log('Using fallback component (dummy detected)')
            } else {
              this.autocompleteLoaded = true
              this.useFallback = false
              console.log('Using vuetify-google-autocomplete component')
            }
          } else {
            this.useFallback = true
            console.log('Using fallback component (no render function)')
          }
        } else {
          this.useFallback = true
          console.log('Using fallback component (not registered)')
        }
      } catch (error) {
        console.warn('Error checking autocomplete availability:', error)
        this.useFallback = true
      }

      // Force fallback for now to ensure we get the Google Maps autocomplete
      this.useFallback = true
      console.log('Forcing fallback mode for testing')

      console.log('Component state:', {
        autocompleteLoaded: this.autocompleteLoaded,
        useFallback: this.useFallback,
        googleMapsLoaded: this.googleMapsLoaded
      })
    },

    loadGoogleMaps () {
      // Load Google Maps API if not already loaded
      if (window.google && window.google.maps) {
        this.googleMapsLoaded = true
        this.$nextTick(() => {
          this.initFallbackAutocomplete()
        })
        return
      }

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&v=${this.version}`
      script.async = true
      script.defer = true
      script.onload = () => {
        this.googleMapsLoaded = true
        this.initFallbackAutocomplete()
      }
      script.onerror = () => {
        console.error('Failed to load Google Maps API')
      }
      document.head.appendChild(script)
    },

    initFallbackAutocomplete () {
      console.log('initFallbackAutocomplete called:', {
        googleMapsLoaded: this.googleMapsLoaded,
        useFallback: this.useFallback
      })

      if (!this.googleMapsLoaded || !this.useFallback) {
        console.log('Skipping fallback autocomplete initialization:', {
          reason: !this.googleMapsLoaded ? 'Google Maps not loaded' : 'Not using fallback'
        })
        return
      }

      this.$nextTick(() => {
        const input = this.$el.querySelector('.fallback-input input')
        console.log('Looking for input element:', input)

        if (input && window.google && window.google.maps) {
          console.log('Initializing Google Maps Autocomplete on input')

          // Build componentRestrictions object
          const restrictions = { ...this.componentRestrictions }

          // Handle country restrictions properly
          if (this.country) {
            if (Array.isArray(this.country)) {
              restrictions.country = this.country
            } else if (typeof this.country === 'string' && this.country.trim()) {
              restrictions.country = this.country
            }
          }

          const options = {
            types: this.types,
            componentRestrictions: restrictions
          }
          if (this.bounds && typeof this.bounds.south === 'number' && typeof this.bounds.north === 'number' &&
              typeof this.bounds.west === 'number' && typeof this.bounds.east === 'number') {
            options.bounds = new window.google.maps.LatLngBounds(
              { lat: this.bounds.south, lng: this.bounds.west },
              { lat: this.bounds.north, lng: this.bounds.east }
            )
          }

          console.log('Autocomplete options:', { types: options.types, componentRestrictions: options.componentRestrictions, bounds: !!options.bounds })

          const autocomplete = new window.google.maps.places.Autocomplete(input, options)

          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace()
            if (place.geometry || (place.address_components && place.address_components.length > 0)) {
              this.onPlaceChanged(place)
            }
          })

          console.log('Google Maps Autocomplete initialized successfully')
        } else {
          console.log('Cannot initialize autocomplete:', {
            input: !!input,
            google: !!window.google,
            maps: !!(window.google && window.google.maps)
          })
        }
      })
    },

    onPlaceChanged (place) {
      console.log('Raw Google Places data:', place)

      // Parse the Google Places data into a consistent format
      const parsedData = this.parseGooglePlaceData(place)

      // Emit the parsed data
      this.$emit('place_changed', parsedData)

      // Update the input value with the formatted address
      if (place.formatted_address) {
        this.searchValue = place.formatted_address
        this.$emit('input', place.formatted_address)
      }
    },

    parseGooglePlaceData (place) {
      // Initialize parsed data with the original place object
      const parsedData = { ...place }

      // Extract address components
      if (place.address_components) {
        place.address_components.forEach((component) => {
          const types = component.types

          if (types.includes('street_number')) {
            parsedData.street_number = component.long_name
          } else if (types.includes('route')) {
            parsedData.route = component.long_name
          } else if (types.includes('locality')) {
            parsedData.locality = component.long_name
            parsedData.city = component.long_name
          } else if (types.includes('administrative_area_level_2')) {
            parsedData.administrative_area_level_2 = component.long_name
          } else if (types.includes('administrative_area_level_1')) {
            parsedData.administrative_area_level_1 = component.long_name
            parsedData.administrative_area_level_1_short = component.short_name
            parsedData.province = component.long_name
            parsedData.state = component.long_name
          } else if (types.includes('country')) {
            parsedData.country = component.long_name
            parsedData.country_code = component.short_name
          } else if (types.includes('postal_code')) {
            parsedData.postal_code = component.long_name
            // Extract the basic postal code format (first 3 characters for Ontario)
            if (component.long_name && component.long_name.length >= 3) {
              parsedData.postal_code_basic = component.long_name.substring(0, 3)
            }
          } else if (types.includes('sublocality')) {
            parsedData.sublocality = component.long_name
          } else if (types.includes('neighborhood')) {
            parsedData.neighborhood = component.long_name
          }
        })
      }

      // Extract coordinates
      if (place.geometry && place.geometry.location) {
        parsedData.latitude = place.geometry.location.lat()
        parsedData.longitude = place.geometry.location.lng()
      }

      // Create a name field if it doesn't exist
      if (!parsedData.name && place.formatted_address) {
        // Extract the street address part (before the first comma)
        const parts = place.formatted_address.split(',')
        if (parts.length > 0) {
          parsedData.name = parts[0].trim()
        }
      }

      // Ensure we have the basic required fields
      parsedData.formatted_address = place.formatted_address || ''
      parsedData.place_id = place.place_id || ''

      console.log('Parsed address data:', parsedData)
      return parsedData
    },

    onError (error) {
      console.error('Google Autocomplete error:', error)
      this.$emit('error', error)
    },

    onInput (value) {
      this.$emit('input', value)
    },

    onChange (value) {
      this.$emit('change', value)
    },
    clear () {
      this.searchValue = ''
      this.$emit('input', '')
    }
  }
}
</script>

<style scoped>
.google-autocomplete-wrapper {
  width: 100%;
}

.google-autocomplete-input,
.fallback-input {
  width: 100%;
}

.fallback-input {
  position: relative;
}
</style>
